/* @flow */

import { DateTime } from 'luxon';
import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { isString } from '@e180/util/lib/type';

import useTimeFormat from './hook/useTimeFormat';
import messages from 'src/shared/app/base/date/l10n/dateL10n';
import {
  formatDay,
  formatWeekDay,
  formatDateTime,
  formatTime,
} from 'src/shared/app/base/date/util/dateFormatters';
import { isDateToday } from 'src/shared/app/base/date/util/dateUtils';

type Props = {
  date: DateTime | string,
  id?: string,
  withoutMonth?: boolean,
  withToday?: boolean,
  withLocalZone?: boolean,
};

/* if today && withToday = Today, Time
  if !today && withToday = Weekday date, Time => Wed 22, 10:00
  if !withToday = Weekday, Month Date, Time => Wed, April 24, 10:00
  if withoutMonth = Weekday date, Time => Wed 22, 10:00
 */
const DateTimeComp = ({
  date: propDate,
  id,
  withoutMonth,
  withToday,
  withLocalZone,
}: Props): React.Node => {
  const date: DateTime = isString(propDate)
    ? DateTime.fromISO(propDate)
    : propDate;

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();
  const { locale } = intl;
  const timeFormat = useTimeFormat();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  let txt;

  if (withToday) {
    if (isDateToday(date)) {
      txt = `${intl.formatMessage(messages.today)}, ${formatTime(
        date,
        locale,
        timeFormat,
        withLocalZone,
      )}`;
    } else if (withoutMonth) {
      txt = `${formatWeekDay(date, locale)} ${formatDay(
        date,
        locale,
      )}, ${formatTime(date, locale, timeFormat, withLocalZone)}`;
    } else {
      txt = formatDateTime(date, locale, timeFormat, withLocalZone);
    }
  } else if (withoutMonth) {
    txt = `${formatWeekDay(date, locale)} ${formatDay(
      date,
      locale,
    )}, ${formatTime(date, locale, timeFormat, withLocalZone)}`;
  } else {
    txt = formatDateTime(date, locale, timeFormat, withLocalZone);
  }

  return (
    <time id={id} dateTime={date.toISO()} aria-label={txt}>
      {txt}
    </time>
  );
};

export default compose(React.memo)(DateTimeComp);
